import React, { cloneElement, isValidElement, useCallback, useEffect, useId, useRef, useState } from "react";
import { FiAlertCircle } from "react-icons/fi";
import { opiszBladSerwera } from "../../../utils/bladSerwera";
import s from "./PoleFormularza.module.css";

/**
 * POLE FORMULARZA Z BŁĘDEM PRZY POLU — jedno dla całej aplikacji.
 *
 * [U11, audyt całości 2026-09-25] Właściciel: „system nie wymaga nauczania, tylko user ma podane
 * na tacy to, co ma robić, zgodnie ze wzorcami projektowania".
 *
 * <b>Co było źle.</b> Szuflady celów i karty e-teczki sprawdzały pola, a wynik pokazywały
 * WYŁĄCZNIE znikającym tostem („Notatka musi mieć tytuł i treść.", „Podaj datę, od której umowa
 * obowiązuje."). Po trzech sekundach tost znikał, a formularz nie miał ŻADNEGO śladu, które pole
 * jest nie tak — człowiek czytał komunikat, szukał pola wzrokiem i zgadywał. Odmowa serwera
 * z listą pól (`errors: { Pole: [...] }`) kończyła tak samo: wszystkie powody sklejone w jeden tost.
 *
 * <b>Kontrakt.</b>
 *  • etykieta `<label for>` wskazuje pole (klik w etykietę = kursor w polu);
 *  • komunikat stoi POD polem, ma własne `id`, a pole dostaje `aria-invalid="true"`
 *    i `aria-describedby` na ten komunikat — czytnik ekranu przeczyta powód razem z polem;
 *  • po nieudanym zapisie kursor ląduje w PIERWSZYM błędnym polu (kolejność z ekranu, nie z kodu),
 *    a szuflada przewija się do niego — komunikat poniżej krawędzi ekranu to znów „błąd i tyle";
 *  • tost zostaje TYLKO dla błędów ogólnych (brak uprawnień, awaria, powód bez wskazania pola).
 *
 * Pole dostaje atrybuty przez `cloneElement` — dziecko może być zwykłym `<input>`, `<select>`,
 * `<textarea>` albo komponentem, który je przekazuje dalej (`DatePicker`, `WyborOsoby`).
 * Tryb `grupa` jest dla pól złożonych z wielu kontrolek (lista osób, kamienie milowe, gwiazdki):
 * otoczka dostaje `role="group"`, a etykieta i komunikat są do niej przypięte.
 * Gdy kontrolka stoi w układzie (suwak z wartością obok), `children` może być FUNKCJĄ: dostaje
 * gotowe atrybuty pola (`id`, `aria-invalid`, `aria-describedby`) i sama je rozkłada.
 *
 * Wygląd pola i etykiety zostaje przy module (klasy przez `className` / `klasaEtykiety`), bo każdy
 * moduł ma już swoją siatkę formularza; ten komponent dokłada wyłącznie stan błędu.
 */

/** Kontrolki, na które da się postawić kursor — w tej kolejności szukamy celu fokusu w otoczce. */
const KONTROLKI =
    "input:not([type=\"hidden\"]):not([disabled]), select:not([disabled]), textarea:not([disabled]), "
    + "button:not([disabled]), [tabindex]:not([tabindex=\"-1\"])";

/**
 * Przewija do pierwszego błędnego pola w `kontener` (albo w całym dokumencie) i stawia w nim kursor.
 * Kolejność = kolejność w DOM, czyli ta, którą człowiek widzi na ekranie.
 *
 * @returns {Element|null} element, który dostał fokus
 */
export const skupPierwszyBlad = (kontener) => {
    const korzen = kontener && typeof kontener.querySelector === "function"
        ? kontener
        : (typeof document !== "undefined" ? document : null);
    if (!korzen) return null;
    const otoczka = korzen.querySelector("[data-pole-bledne=\"true\"]");
    if (!otoczka) return null;
    const cel = otoczka.querySelector("[aria-invalid=\"true\"]")
        || otoczka.querySelector(KONTROLKI)
        || otoczka;
    if (typeof cel.scrollIntoView === "function") cel.scrollIntoView({ block: "center", behavior: "smooth" });
    if (typeof cel.focus === "function") cel.focus({ preventScroll: true });
    return cel;
};

const tekst = (v) => (typeof v === "string" && v.trim() ? v.trim() : null);

/**
 * Klucz z odpowiedzi serwera → nazwa pola formularza.
 *  • `$.targetValue` — błąd wiązania JSON (ścieżka JSON),
 *  • `dto.Title` — błąd modelu z nazwą parametru akcji,
 *  • `Milestones[0].Title` — pozycja listy: komunikat idzie do pola całej listy.
 */
const krotkiKlucz = (klucz) => String(klucz ?? "")
    .replace(/^\$\.?/, "")
    .replace(/^(dto|request|model|body|dane)\./i, "")
    .split(/[.[]/)[0]
    .trim();

/**
 * Odmowa serwera → { pola: { nazwaPola: zdanie }, ogolny: zdanie | null }.
 *
 * Format serwera (czytany, nie zgadywany): `GlobalExceptionFilter.HandleValidationException`
 * (FluentValidation) i `InvalidModelStateResponseFactory` (wiązanie modelu) oddają 400
 * z `errors: { Pole: ["powód", …] }`. Klucze bywają w PascalCase (`Title`), jako ścieżka JSON
 * (`$.title`) albo z przedrostkiem parametru (`dto.Title`) — porównujemy bez wielkości liter.
 *
 * @param {object} err       błąd z axiosa
 * @param {object} opcje
 * @param {string[]} [opcje.pola]         nazwy pól formularza (camelCase, jak w stanie formularza)
 * @param {object}   [opcje.mapa]         klucz serwera → nazwa pola, gdy się różnią (`ownerUserIds` → `osoby`)
 * @param {object}   [opcje.kody]         `code` / `reason` odpowiedzi → pole (`{ error, reason: "pesel" }`
 *        z ETeczkaController, `{ message, code: "EMPLOYEE_NUMBER_TAKEN" }` z AdminUsersController)
 * @param {string|function} [opcje.poleDomyslne]  dla 400 BEZ listy pól (samo `message`): nazwa pola,
 *        którego dotyczy cały formularz (okno z jednym polem powodu), albo funkcja zdanie → pole|null.
 *        Wynik spoza `pola`/`mapa`/`kody` (gdy któraś lista jest podana) idzie do komunikatu ogólnego —
 *        zdanie pod polem, którego na ekranie nie ma, byłoby zgubione.
 * @param {function} [opcje.opisz]        (err) → zdanie ogólne; domyślnie `opiszBladSerwera`
 * @param {string}   [opcje.domyslny]     zdanie, gdy serwer nie powiedział nic
 */
export const bledyZOdpowiedzi = (err, {
    pola = [], mapa = {}, kody = {}, poleDomyslne = null, opisz = null, domyslny = "Nie udało się zapisać zmian.",
} = {}) => {
    const status = err?.response?.status;
    const dane = err?.response?.data;
    const wynik = {};
    const reszta = [];

    const zMapy = (k) => {
        const trafienie = Object.keys(mapa).find((m) => m.toLowerCase() === k.toLowerCase());
        return trafienie ? mapa[trafienie] : null;
    };
    const znajdz = (klucz) => {
        const pelny = String(klucz ?? "").replace(/^\$\.?/, "");
        const krotki = krotkiKlucz(klucz);
        if (!krotki) return null;
        return zMapy(pelny) || zMapy(krotki) || pola.find((p) => p.toLowerCase() === krotki.toLowerCase()) || null;
    };

    if (status === 400 && dane && typeof dane === "object"
        && dane.errors && typeof dane.errors === "object" && !Array.isArray(dane.errors)) {
        Object.entries(dane.errors).forEach(([klucz, lista]) => {
            const zdania = (Array.isArray(lista) ? lista : [lista]).map(tekst).filter(Boolean);
            if (zdania.length === 0) return;
            const pole = znajdz(klucz);
            if (pole) wynik[pole] = [wynik[pole], ...zdania].filter(Boolean).join(" ");
            else reszta.push(...zdania);
        });
    }

    const bezListy = status === 400 && Object.keys(wynik).length === 0 && reszta.length === 0;
    // `Message` z wielkiej litery: tak wychodzi ApiErrorResponse z części starszych ścieżek.
    const zdanieOdpowiedzi = tekst(dane) || tekst(dane?.message) || tekst(dane?.error) || tekst(dane?.Message);
    const dozwolone = new Set([...pola, ...Object.values(mapa), ...Object.values(kody)]);
    const wolno = (pole) => Boolean(pole) && (dozwolone.size === 0 || dozwolone.has(pole));

    if (bezListy && dane && typeof dane === "object" && zdanieOdpowiedzi) {
        const kod = [dane.code, dane.reason].map(tekst).filter(Boolean)
            .map((k) => Object.keys(kody).find((m) => m.toLowerCase() === k.toLowerCase()))
            .find(Boolean);
        if (kod && wolno(kody[kod])) wynik[kody[kod]] = zdanieOdpowiedzi;
    }

    if (bezListy && poleDomyslne && Object.keys(wynik).length === 0 && zdanieOdpowiedzi) {
        const pole = typeof poleDomyslne === "function" ? poleDomyslne(zdanieOdpowiedzi) : poleDomyslne;
        if (wolno(pole)) wynik[pole] = zdanieOdpowiedzi;
    }

    const maPola = Object.keys(wynik).length > 0;
    let ogolny = null;
    if (reszta.length > 0) ogolny = reszta.join(" ");
    else if (!maPola) ogolny = typeof opisz === "function" ? opisz(err) : opiszBladSerwera(err, domyslny);
    return { pola: wynik, ogolny };
};

const bezPustych = (bledy) => Object.fromEntries(
    Object.entries(bledy || {}).filter(([, v]) => typeof v === "string" ? v.trim() : Boolean(v)),
);

/** Te same pola z tymi samymi zdaniami — wtedy zostawiamy stary obiekt (bez zbędnego rysowania). */
const takieSame = (a, b) => {
    const ka = Object.keys(a);
    return ka.length === Object.keys(b).length && ka.every((k) => a[k] === b[k]);
};

/**
 * Stan błędów formularza. `pokazBledy` = walidacja nie przeszła (fokus na pierwsze błędne pole),
 * `ustawBledy` = cicha zmiana (np. gaszenie komunikatu po poprawce), `obsluzBladSerwera` = odmowa
 * serwera rozłożona na pola; zwraca zdanie OGÓLNE (do tostu) albo `null`, gdy wszystko trafiło do pól.
 *
 * `kontenerRef` przypinamy do formularza — bez niego fokus szuka w całym dokumencie.
 */
export const useBledyPol = () => {
    const [bledy, setBledy] = useState({});
    const [proba, setProba] = useState(0);
    const kontenerRef = useRef(null);

    useEffect(() => {
        if (proba > 0) skupPierwszyBlad(kontenerRef.current);
    }, [proba]);

    const pokazBledy = useCallback((nowe) => {
        const czyste = bezPustych(nowe);
        setBledy(czyste);
        const sa = Object.keys(czyste).length > 0;
        if (sa) setProba((p) => p + 1);
        return sa;
    }, []);

    const ustawBledy = useCallback((nowe) => {
        setBledy((poprzednie) => {
            const czyste = bezPustych(typeof nowe === "function" ? nowe(poprzednie) : nowe);
            return takieSame(poprzednie, czyste) ? poprzednie : czyste;
        });
    }, []);

    const wyczyscBlad = useCallback((pole) => {
        setBledy((b) => {
            if (!pole) return Object.keys(b).length === 0 ? b : {};
            if (!b[pole]) return b;
            const reszta = { ...b };
            delete reszta[pole];
            return reszta;
        });
    }, []);

    const obsluzBladSerwera = useCallback((err, opcje) => {
        const { pola, ogolny } = bledyZOdpowiedzi(err, opcje);
        if (Object.keys(pola).length > 0) pokazBledy(pola);
        return ogolny;
    }, [pokazBledy]);

    return { bledy, pokazBledy, ustawBledy, wyczyscBlad, obsluzBladSerwera, kontenerRef };
};

/** `useId` w React 19 oddaje znaki spoza [A-Za-z0-9_-] — do `id` i `aria-describedby` bierzemy bezpieczne. */
const bezpieczneId = (surowe) => `pole-${String(surowe).replace(/[^A-Za-z0-9_-]/g, "")}`;

const PoleFormularza = ({
    etykieta = null,
    blad = null,
    podpowiedz = null,
    id,
    nazwa,
    wymagane = false,
    grupa = false,
    className = "",
    klasaEtykiety = "",
    klasaPodpowiedzi = "",
    // Stały identyfikator komunikatu dla zapadek (np. „pesel-blad" pilnowane także testem backendu O-17).
    testIdBledu,
    children,
}) => {
    const wlasne = useId();
    const zFunkcji = !grupa && typeof children === "function";
    const dziecko = !grupa && !zFunkcji && isValidElement(children) ? children : null;
    const bazaId = id || dziecko?.props?.id || bezpieczneId(wlasne);
    const idBledu = `${bazaId}-blad`;
    const idPodpowiedzi = `${bazaId}-podpowiedz`;
    const idEtykiety = `${bazaId}-etykieta`;
    const bledne = Boolean(blad);

    const opis = [
        dziecko?.props?.["aria-describedby"],
        podpowiedz ? idPodpowiedzi : null,
        bledne ? idBledu : null,
    ].filter(Boolean).join(" ") || undefined;

    const atrybutyPola = {
        id: bazaId,
        "aria-invalid": bledne ? true : dziecko?.props?.["aria-invalid"],
        "aria-describedby": opis,
        ...(wymagane ? { "aria-required": true } : {}),
    };
    let tresc = children;
    if (zFunkcji) tresc = children(atrybutyPola);
    else if (dziecko) tresc = cloneElement(dziecko, atrybutyPola);

    const atrybutyGrupy = grupa
        ? {
            role: "group",
            tabIndex: -1,
            "aria-labelledby": etykieta ? idEtykiety : undefined,
            "aria-describedby": [podpowiedz ? idPodpowiedzi : null, bledne ? idBledu : null].filter(Boolean).join(" ") || undefined,
            "aria-invalid": bledne ? true : undefined,
        }
        : {};

    return (
        <div
            className={[s.pole, className].filter(Boolean).join(" ")}
            data-pole={nazwa || undefined}
            data-pole-bledne={bledne ? "true" : undefined}
            {...atrybutyGrupy}
        >
            {etykieta && (grupa
                ? <span id={idEtykiety} className={klasaEtykiety || undefined}>{etykieta}</span>
                : <label id={idEtykiety} htmlFor={bazaId} className={klasaEtykiety || undefined}>{etykieta}</label>)}
            {tresc}
            {podpowiedz && (
                <span id={idPodpowiedzi} className={klasaPodpowiedzi || s.podpowiedz}>{podpowiedz}</span>
            )}
            {bledne && (
                <span id={idBledu} className={s.blad} data-blad-pola={nazwa || bazaId} data-testid={testIdBledu}>
                    <FiAlertCircle size={12} aria-hidden="true" className={s.bladIkona} />
                    <span>{blad}</span>
                </span>
            )}
        </div>
    );
};

export default PoleFormularza;
